import { useAtom } from 'jotai'
import { useCallback, useEffect } from 'react'
import type { ReserveOptionMenuInput } from '~/lib/graphql/graphql'
import { createScope } from '~/lib/jotai'
import { useSelectedOptionMenus } from '../store/stateMachine/context'
import { useOptionMenus } from '../store/stateMachine/state'
import type { ReserveInputOptionMenu } from '../store/stateMachine/state'

export const scope = createScope('useSelectOptionMenus')

const localSelectedOptionMenus$ = scope.atom<ReserveOptionMenuInput[]>(
  [],
  'localSelectedOptionMenus$'
)

export function useSelectOptionMenus() {
  const options = useOptionMenus()
  const [selectedOptions, setSelectedOptions] = useAtom(localSelectedOptionMenus$)
  const selectedOptionMenus = useSelectedOptionMenus()

  useEffect(() => {
    setSelectedOptions(selectedOptionMenus)
  }, [selectedOptionMenus, setSelectedOptions])

  const onSelect = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>, option: ReserveInputOptionMenu) => {
      const optionMenuId = option.optionMenu.optionMenuId
      const count = elementToValue(e)
      setSelectedOptions((current) => {
        const removed = removeOptionMenu(current, option)
        // 0点を選択した場合は配列から削除し、それ以外は配列に追加する
        return count === 0 ? removed : [...removed, { optionMenuId, count }]
      })
    },
    [setSelectedOptions]
  )

  return { options, selectedOptions, onSelect }
}

function elementToValue(e: React.ChangeEvent<HTMLSelectElement>) {
  //valueは●点の文字列で入っているため、末尾の「点」を取り除く
  return Number(e.target.value.replace(/点$/, ''))
}
if (import.meta.vitest) {
  const { it, expect } = import.meta.vitest
  it('elementToValue', () => {
    expect(
      elementToValue({ target: { value: '1点' } } as React.ChangeEvent<HTMLSelectElement>)
    ).toBe(1)
    expect(
      elementToValue({ target: { value: '10点' } } as React.ChangeEvent<HTMLSelectElement>)
    ).toBe(10)
  })
}

function removeOptionMenu(
  selectedOptions: ReserveOptionMenuInput[],
  option: ReserveInputOptionMenu
) {
  return selectedOptions.filter((v) => v.optionMenuId !== option.optionMenu.optionMenuId)
}
if (import.meta.vitest) {
  const { it, expect } = import.meta.vitest
  const selectedOptions = [
    { optionMenuId: '1', count: 1 },
    { optionMenuId: '2', count: 2 },
  ] as ReserveOptionMenuInput[]
  it('removeOptionMenu (existing removed option)', () => {
    const option = { optionMenu: { optionMenuId: '1' } } as ReserveInputOptionMenu
    expect(removeOptionMenu(selectedOptions, option)).toEqual([
      { optionMenuId: '2', count: 2 },
    ] as ReserveOptionMenuInput[])
  })
  it('removeOptionMenu (non-existing removed option)', () => {
    const option = { optionMenu: { optionMenuId: '3' } } as ReserveInputOptionMenu
    expect(removeOptionMenu(selectedOptions, option)).toEqual([
      { optionMenuId: '1', count: 1 },
      { optionMenuId: '2', count: 2 },
    ] as ReserveOptionMenuInput[])
  })
}
